import React from 'react';
import {
	FaUser,
	FaSignOutAlt,
	FaCrown,
	FaCog,
	FaCreditCard,
	FaChevronRight,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useCompanySettings } from '../../hooks/useCompany';
import {
	Text,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverBody,
	Avatar,
	Badge,
	Button,
	Box,
	Spinner,
	Flex,
	VStack,
	HStack,
	Divider,
	Icon,
	useBreakpointValue,
} from '@chakra-ui/react';
import brandColor from '../../styles/Brand';
import { getCompanyLogoUrl } from '../../utils/companyLogo';

const ROLE_BADGE = {
	'Company Admin': { colorScheme: 'teal', label: 'Company Admin' },
	Employee: { colorScheme: 'blue', label: 'Employee' },
};

const avatarImageSx = (isUserPhoto) => ({
	aspectRatio: '1 / 1',
	flexShrink: 0,
	'& img': {
		aspectRatio: '1 / 1',
		width: '100%',
		height: '100%',
		objectFit: isUserPhoto ? 'cover' : 'contain',
		objectPosition: 'center',
	},
});

const ProfileActionRow = ({ icon, label, onClick }) => (
	<Button
		variant='ghost'
		w='full'
		justifyContent='flex-start'
		leftIcon={<Icon as={icon} boxSize={4} color='gray.500' />}
		rightIcon={<Icon as={FaChevronRight} boxSize={3} color='gray.300' />}
		fontWeight='medium'
		fontSize='sm'
		color='gray.700'
		h='40px'
		px={3}
		rounded='lg'
		_hover={{ bg: 'teal.50', color: 'teal.700' }}
		_active={{ bg: 'teal.100' }}
		onClick={onClick}>
		{label}
	</Button>
);

const formatBillingSummary = (subscription) => {
	if (!subscription?.nextBillingDate && !subscription?.amount) return null;

	const parts = [];
	if (subscription.nextBillingDate) {
		parts.push(
			`Renews ${new Date(subscription.nextBillingDate).toLocaleDateString(undefined, {
				month: 'short',
				day: 'numeric',
			})}`,
		);
	}
	if (subscription.amount) {
		const currency = subscription.currency?.toUpperCase() || '';
		parts.push(`${subscription.amount}${currency ? ` ${currency}` : ''}`);
	}
	return parts.join(' · ');
};

const UserProfileDropdown = () => {
	const navigate = useNavigate();
	const popoverTrigger = useBreakpointValue({ base: 'click', md: 'hover' }) || 'click';

	const {
		user: userData,
		companyId,
		userId,
		company,
		logout,
		userRole,
		userEmail,
		profileImage,
	} = useAuthenticatedUserInfo();

	const { data: subscription } = useSubscription(companyId);
	const { data: companySettings } = useCompanySettings(companyId);

	if (!userData) {
		return (
			<Flex align='center' justify='center' px={2}>
				<Spinner size='sm' color={brandColor.blue} />
			</Flex>
		);
	}

	const user = userData.data || null;
	const companyData = company?.data || company;
	const companyObj = companyData;
	const userProfileImage = profileImage || user?.profileImage || null;
	const companyLogo =
		companySettings?.data?.settings?.branding?.logo ||
		getCompanyLogoUrl(companyObj) ||
		null;
	const avatarSrc = userProfileImage || companyLogo || undefined;
	const isUserPhoto = Boolean(userProfileImage);
	const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';
	const roleBadge = ROLE_BADGE[userRole] || { colorScheme: 'gray', label: userRole };

	const hasPlanInfo = Boolean(subscription?.planName || subscription?.status);
	const billingSummary = formatBillingSummary(subscription);

	const workspaceSummary = [
		companyObj?.name,
		hasPlanInfo
			? `${subscription.planName || 'Free'}${subscription.status ? ` · ${subscription.status}` : ''}`
			: null,
	]
		.filter(Boolean)
		.join(' · ');

	const navigateAndClose = (path, closePopover) => {
		closePopover();
		navigate(path);
	};

	return (
		<Popover
			placement='bottom-end'
			trigger={popoverTrigger}
			openDelay={80}
			closeDelay={150}
			gutter={8}>
			{({ isOpen, onClose: closePopover }) => (
				<>
					<PopoverTrigger>
						<Button
							variant='ghost'
							p={0}
							minW='auto'
							h='auto'
							rounded='lg'
							_hover={{ bg: 'white' }}
							_active={{ bg: 'white' }}>
							<Avatar
								size='sm'
								src={avatarSrc}
								name={fullName}
								icon={<FaUser />}
								cursor='pointer'
								border='2px solid'
								borderColor={isOpen ? 'teal.400' : 'transparent'}
								transition='border-color 0.2s'
								sx={avatarImageSx(isUserPhoto)}
							/>
						</Button>
					</PopoverTrigger>

					<PopoverContent
						w='300px'
						maxW='95vw'
						border='1px solid'
						borderColor='gray.100'
						rounded='xl'
						boxShadow='0 16px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)'
						overflow='hidden'
						_focus={{ boxShadow: '0 16px 40px rgba(0,0,0,0.12)' }}>
						<Box
							bg='linear-gradient(135deg, rgba(20,184,166,0.12) 0%, rgba(44,128,223,0.08) 100%)'
							px={4}
							py={4}
							borderBottom='1px solid'
							borderColor='gray.100'>
							<HStack spacing={3} align='flex-start'>
								<Avatar
									size='md'
									src={avatarSrc}
									name={fullName}
									icon={<FaUser />}
									border='2px solid white'
									boxShadow='sm'
									sx={avatarImageSx(isUserPhoto)}
								/>
								<Box minW={0} flex='1'>
									<Text fontWeight='bold' fontSize='md' noOfLines={1} mb={1}>
										{fullName}
									</Text>
									<Text fontSize='xs' color='gray.600' noOfLines={1} mb={2}>
										{userEmail}
									</Text>
									{userRole && (
										<Badge
											colorScheme={roleBadge.colorScheme}
											borderRadius='full'
											px={2}
											py={0.5}
											fontSize='10px'
											textTransform='none'>
											{roleBadge.label}
										</Badge>
									)}
								</Box>
							</HStack>
						</Box>

						<PopoverBody p={2}>
							<VStack align='stretch' spacing={0}>
								<ProfileActionRow
									icon={FaUser}
									label='View profile'
									onClick={() => navigateAndClose(`/employee/view/${userId}`, closePopover)}
								/>
								<ProfileActionRow
									icon={FaCreditCard}
									label='Manage subscription'
									onClick={() => navigateAndClose('/manage-subscription', closePopover)}
								/>
								<ProfileActionRow
									icon={FaCog}
									label='Settings'
									onClick={() => navigateAndClose('/settings', closePopover)}
								/>

								{workspaceSummary && (
									<>
										<Divider my={2} />
										<Box px={3} py={2}>
											<HStack spacing={2} align='flex-start'>
												<Icon as={FaCrown} boxSize={3.5} color='teal.500' mt={0.5} flexShrink={0} />
												<Box minW={0}>
													<Text fontSize='xs' color='gray.600' noOfLines={2} mb={0}>
														{workspaceSummary}
													</Text>
													{billingSummary && (
														<Text fontSize='xs' color='gray.400' noOfLines={1} mt={0.5} mb={0}>
															{billingSummary}
														</Text>
													)}
												</Box>
											</HStack>
										</Box>
									</>
								)}

								<Divider my={2} />

								<Button
									variant='ghost'
									w='full'
									justifyContent='flex-start'
									leftIcon={<Icon as={FaSignOutAlt} boxSize={4} />}
									fontWeight='medium'
									fontSize='sm'
									color='red.600'
									h='40px'
									px={3}
									rounded='lg'
									_hover={{ bg: 'red.50', color: 'red.700' }}
									_active={{ bg: 'red.100' }}
									onClick={() => {
										closePopover();
										logout();
									}}>
									Sign out
								</Button>
							</VStack>
						</PopoverBody>
					</PopoverContent>
				</>
			)}
		</Popover>
	);
};

export default UserProfileDropdown;
